/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Popover,
  Grid,
  Snackbar,
  Alert,
  Collapse,
  ListItemSecondaryAction,
  Tooltip,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  GetApp,
  Settings,
  ExpandMore,
  ExpandLess,
  Add,
  FileUpload,
  FileDownload,
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptLocale from '@fullcalendar/core/locales/pt';
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import moment from 'moment';
import 'moment/locale/pt';
import 'moment/locale/fr';
import BaseDialog from '../base-dialog';


// Helper to get locale object (handle both default export and direct export)
const getLocaleObject = (locale) => {
  if (!locale) return null;
  // Handle ES module default export
  if (locale.default && typeof locale.default === 'object') {
    return locale.default;
  }
  // Handle direct export
  if (locale.code) {
    return locale;
  }
  return locale;
};

// Get locale code for Intl API
const getLocaleCode = (language) => {
  if (language === 'pt') return 'pt-PT';
  if (language === 'fr') return 'fr-FR';
  return 'en-GB';
};

// Neutral color for controllers without an assigned color
const UNASSIGNED_COLOR = '#757575';

// Special ID for unassigned filter option
const UNASSIGNED_FILTER_ID = '__unassigned__';
// Special ID for select all option
const SELECT_ALL_ID = '__select_all__';

// Color for overdue (past + not completed) events
const OVERDUE_COLOR = '#d32f2f';

// Conformity values (matching backend)
const CONFORMITY = {
  NOT_OK: 0,
  OK: 1,
  WAITING_RESULT: 2,
  DOES_NOT_APPLY: 3,
};

// Control status values (matching backend)
const CONTROL_STATUS = {
  ACTIVE: 1,
  CANCELED: 2,
};

// Helper to check if an event date is in the past
const isEventPast = (eventDate) => {
  const today = moment().startOf('day');
  const eventDay = moment(eventDate).startOf('day');
  return eventDay.isBefore(today);
};

// Helper to check if a date is today or in the future
const isDateTodayOrFuture = (date) => {
  const today = moment().startOf('day');
  const targetDay = moment(date).startOf('day');
  return targetDay.isSameOrAfter(today);
};

// Helper to check if an event is completed (has a result)
const isEventCompleted = (conformity) =>
  // Completed if conformity is not WAITING_RESULT (2) and not null/undefined
  conformity !== null &&
  conformity !== undefined &&
  conformity !== CONFORMITY.WAITING_RESULT
  ;

// Helper to convert hex to rgba with opacity
const hexToRgba = (hex, opacity) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

InspectionsCalendar.propTypes = {
  title: PropTypes.string,
  fetchEventsFunction: PropTypes.func.isRequired,
  fetchControllersFunction: PropTypes.func, // () => Promise<{ controllers: [...] }>
  updateControllerColorFunction: PropTypes.func, // (userId, color) => Promise<{ user: {...} }>
  updateControllerVisibilityFunction: PropTypes.func, // (userId, visible) => Promise<{ user: {...} }>
  updateEventDateFunction: PropTypes.func, // (type, id, newDate) => Promise<{ event: {...} }>
  cancelEventFunction: PropTypes.func, // (type, id) => Promise<{ event: {...} }> - cancel behind schedule control
  assignControllerFunction: PropTypes.func, // (type, id, controllerId) => Promise<{ control: {...} }> - assign controller to control
  exportCSVFunction: PropTypes.func, // (events, { t, selectedDate, isEventPast, isEventCompleted, CONTROL_STATUS }) => void - custom CSV export
  onError: PropTypes.func, // (message) => void - callback for error handling (e.g., show notification)
  onControlClick: PropTypes.func,
  onDateClick: PropTypes.func,
  onCreateControl: PropTypes.func, // (date) => void - callback to create a new control for the selected date
  memberId: PropTypes.number,
  height: PropTypes.string,
  itemLabelSingular: PropTypes.string,
  itemLabelPlural: PropTypes.string,
  calendarSx: PropTypes.object,
  renderControlDetails: PropTypes.func, // (control, { onClose, onViewFullDetails, onCancel }) => ReactNode
  renderEventListItem: PropTypes.func, // (event, { controllerColors, t }) => { primary: ReactNode, secondary: ReactNode } - custom list item content
  getEventTitle: PropTypes.func, // (event, { itemLabel }) => string - custom event title (default: controller name)
  primaryColor: PropTypes.string, // Main color for icons and accents
  titleColor: PropTypes.string, // Color for the title text
  titleFontSize: PropTypes.string, // Font size for the title (e.g., '18px')
  showControllerFilter: PropTypes.bool, // Show/hide the controller filter (default: true)
  canRescheduleEvents: PropTypes.bool, // Allow drag and drop to reschedule events (default: true)
  canCancelOverdueEvents: PropTypes.bool, // Allow canceling overdue events (default: true)
  canCreateControl: PropTypes.bool, // Allow creating controls from the calendar (default: true if onCreateControl is provided)
  canAssignController: PropTypes.bool, // Allow assigning controllers to unassigned controls (default: true if assignControllerFunction is provided)
  // Controlled state props for URL persistence
  defaultView: PropTypes.string, // Initial view type ('dayGridMonth', 'timeGridWeek', 'timeGridDay')
  onViewChange: PropTypes.func, // (viewName) => void - callback when view changes
  defaultDate: PropTypes.instanceOf(Date), // Initial date to display
  onDateChange: PropTypes.func, // (date) => void - callback when displayed date changes
  defaultSelectedControllers: PropTypes.array, // Initial selected controller IDs
  onSelectedControllersChange: PropTypes.func, // (controllerIds) => void - callback when selected controllers change
  onRefreshRef: PropTypes.func, // (refreshFn) => void - callback to receive a refresh function for external use
  onImportCalendar: PropTypes.func, // () => void - callback when user clicks "Import calendar" button
  onExportTemplate: PropTypes.func, // () => void - callback when user clicks "Export template" button
};

/**
 * InspectionsCalendar - A calendar component for displaying controls/inspections
 *
 * @param {function} fetchEventsFunction - Function to fetch events.
 *   Should accept (params) and return a Promise that resolves to { events: [...] }
 *   params: { startDate, endDate, controllerIds?, memberId? }
 */
export default function InspectionsCalendar({
  title = '',
  fetchEventsFunction,
  fetchControllersFunction = null,
  updateControllerColorFunction = null,
  updateControllerVisibilityFunction = null,
  updateEventDateFunction = null,
  cancelEventFunction = null,
  assignControllerFunction = null,
  exportCSVFunction = null,
  onError = null,
  onControlClick,
  onDateClick,
  onCreateControl = null,
  memberId = null,
  height = '600px',
  itemLabelSingular = null,
  itemLabelPlural = null,
  calendarSx = {},
  renderControlDetails = null,
  renderEventListItem = null,
  getEventTitle = null,
  primaryColor = null,
  titleColor = null,
  titleFontSize = null,
  showControllerFilter = true,
  canRescheduleEvents = true,
  canCancelOverdueEvents = true,
  canCreateControl = true,
  canAssignController = true,
  defaultView = 'dayGridMonth',
  onViewChange = null,
  defaultDate = null,
  onDateChange = null,
  defaultSelectedControllers = null,
  onSelectedControllersChange = null,
  onRefreshRef = null,
  onImportCalendar = null,
  onExportTemplate = null,
}) {
  const theme = useTheme();
  const mainColor = primaryColor || theme.palette.primary.main;
  const { t, i18n } = useTranslation();
  const calendarRef = useRef(null);

  // Store initial values in refs so the FullCalendar key doesn't change on user interaction
  const initialViewRef = useRef(defaultView);
  const initialDateRef = useRef(defaultDate);

  // Normalize language code for moment.js
  const normalizedLangForMoment = i18n.language?.split('-')[0] || 'en';

  // Set moment locale on initial render
  if (normalizedLangForMoment === 'pt') {
    moment.locale('pt');
  } else if (normalizedLangForMoment === 'fr') {
    moment.locale('fr');
  } else {
    moment.locale('en');
  }

  // Get FullCalendar locale based on i18n language
  const getFullCalendarLocale = useCallback(() => {
    // Normalize language code (handle variants like 'pt-BR', 'pt-PT', etc.)
    const normalizedLang = i18n.language?.split('-')[0] || 'en';

    const localeObj = normalizedLang === 'pt'
      ? getLocaleObject(ptLocale)
      : normalizedLang === 'fr'
        ? getLocaleObject(frLocale)
        : getLocaleObject(enLocale);

    if (!localeObj) return null;

    // FullCalendar v6 uses browser's Intl API for month/day names
    // But we need to provide them explicitly for proper localization
    const localeCode = getLocaleCode(normalizedLang);

    // Create formatters
    const dateFormatter = new Intl.DateTimeFormat(localeCode, { month: 'long' });
    const dayFormatter = new Intl.DateTimeFormat(localeCode, { weekday: 'long' });
    const shortDayFormatter = new Intl.DateTimeFormat(localeCode, { weekday: 'short' });
    const shortMonthFormatter = new Intl.DateTimeFormat(localeCode, { month: 'short' });
    const narrowDayFormatter = new Intl.DateTimeFormat(localeCode, { weekday: 'narrow' });

    // Generate month names (full and short)
    const monthNames = [];
    const monthNamesShort = [];
    for (let i = 0; i < 12; i += 1) {
      const date = new Date(2024, i, 1);
      monthNames.push(dateFormatter.format(date));
      monthNamesShort.push(shortMonthFormatter.format(date));
    }

    // Generate day names starting from the locale's first day of week
    const dayNames = [];
    const dayNamesShort = [];
    const dayNamesMin = [];
    const firstDayOfWeek = localeObj.week?.dow || 1; // Default to Monday (1)

    // Start from a known Monday (January 1, 2024 is a Monday)
    const baseDate = new Date(2024, 0, 1);
    for (let i = 0; i < 7; i += 1) {
      const dayOffset = (firstDayOfWeek + i) % 7;
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + dayOffset);
      dayNames.push(dayFormatter.format(date));
      dayNamesShort.push(shortDayFormatter.format(date));
      dayNamesMin.push(narrowDayFormatter.format(date));
    }

    // FullCalendar v6 expects these properties for localization
    const enhancedLocale = {
      ...localeObj,
      monthNames,
      monthNamesShort,
      dayNames,
      dayNamesShort,
      dayNamesMin,
      code: localeObj.code || normalizedLang
    };

    // Debug logging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('FullCalendar locale debug:', {
        i18nLanguage: i18n.language,
        normalizedLang,
        localeCode,
        localeObjCode: localeObj?.code,
        enhancedLocaleCode: enhancedLocale.code,
        hasMonthNames: !!enhancedLocale.monthNames,
        monthNamesLength: enhancedLocale.monthNames?.length,
        firstMonth: enhancedLocale.monthNames?.[0],
        hasDayNames: !!enhancedLocale.dayNames,
        dayNamesLength: enhancedLocale.dayNames?.length,
        firstDay: enhancedLocale.dayNames?.[0]
      });
    }

    return enhancedLocale;
  }, [i18n.language]);

  // Set moment locale and update FullCalendar locale based on i18n language
  useEffect(() => {
    // Normalize language code
    const normalizedLang = i18n.language?.split('-')[0] || 'en';

    // Set moment locale
    if (normalizedLang === 'pt') {
      moment.locale('pt');
    } else if (normalizedLang === 'fr') {
      moment.locale('fr');
    } else {
      moment.locale('en');
    }

    // Update FullCalendar locale when language changes
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const locale = getFullCalendarLocale();
      calendarApi.setOption('locale', locale);
      // Force a rerender to apply locale changes
      calendarApi.render();
    }
  }, [i18n.language, getFullCalendarLocale]);

  const [view, setViewState] = useState(defaultView || 'dayGridMonth');
  const [currentDate, setCurrentDateState] = useState(defaultDate || new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [controllers, setControllers] = useState([]);
  const [selectedControllersState, setSelectedControllersState] = useState(defaultSelectedControllers || []);

  // Sync FullCalendar with defaultView and defaultDate when they change (e.g., from URL params)
  const initialSyncDoneRef = useRef(false);
  useEffect(() => {
    if (calendarRef.current && !initialSyncDoneRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (defaultView && defaultView !== calendarApi.view.type) {
        calendarApi.changeView(defaultView);
        setViewState(defaultView);
      }
      if (defaultDate) {
        calendarApi.gotoDate(defaultDate);
        setCurrentDateState(defaultDate);
      }
      initialSyncDoneRef.current = true;
    }
  }, [defaultView, defaultDate]);

  // Wrapper functions that also call callback props for URL persistence
  const setView = useCallback((newView) => {
    setViewState(newView);
    if (onViewChange) {
      onViewChange(newView);
    }
  }, [onViewChange]);

  const setCurrentDate = useCallback((newDate) => {
    setCurrentDateState(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  }, [onDateChange]);

  const setSelectedControllers = useCallback((newSelection) => {
    setSelectedControllersState(newSelection);
    if (onSelectedControllersChange) {
      onSelectedControllersChange(newSelection);
    }
  }, [onSelectedControllersChange]);

  // Use the state value for selectedControllers
  const selectedControllers = selectedControllersState;
  const [controllerColors, setControllerColors] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayEvents, setDayEvents] = useState([]);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [selectedControl, setSelectedControl] = useState(null);
  const [controlDialogOpen, setControlDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [tempControllerColors, setTempControllerColors] = useState({});
  const [allControllers, setAllControllers] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [tempControllerVisibility, setTempControllerVisibility] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [expandedEventId, setExpandedEventId] = useState(null);

  // Refs to track filter state (avoids dependency loops in fetchEvents)
  const selectedControllersRef = useRef(defaultSelectedControllers || []);
  const allControllersCountRef = useRef(0);
  // Counter to trigger filter refetch
  const [filterTrigger, setFilterTrigger] = useState(0);

  // Helper to process fetched events - extracts controllers from events for legend
  const processEventsData = useCallback((data) => {
    setEvents(data.events || []);
    // Extract unique controllers and their colors from events (for legend/badges)
    const uniqueControllers = new Map();
    const colorsFromEvents = {};
    (data.events || []).forEach(event => {
      if (event.controller && event.controller.id) {
        if (!uniqueControllers.has(event.controller.id)) {
          uniqueControllers.set(event.controller.id, event.controller);
          if (event.controller.color) {
            colorsFromEvents[event.controller.id] = event.controller.color;
          }
        }
      }
    });
    // Controllers from events are used for the legend/badges
    setControllers(Array.from(uniqueControllers.values()));
    // Update controller colors
    if (Object.keys(colorsFromEvents).length > 0) {
      setControllerColors(prev => ({ ...prev, ...colorsFromEvents }));
    }
    setLoading(false);
  }, []);

  // Fetch calendar events with current filter
  const fetchEvents = useCallback(async (start, end) => {
    setLoading(true);
    try {
      const startDate = moment(start).startOf('day').toISOString();
      const endDate = moment(end).endOf('day').toISOString();

      const params = {
        startDate,
        endDate,
      };

      // Apply controller filter using refs (to avoid dependency loop)
      const selected = selectedControllersRef.current;
      const totalControllers = allControllersCountRef.current;
      // +1 for the unassigned option
      const totalOptions = totalControllers + 1;

      // Check if unassigned is selected
      const includeUnassigned = selected.includes(UNASSIGNED_FILTER_ID);
      // Get actual controller IDs (excluding the special unassigned ID)
      const controllerIds = selected.filter(id => id !== UNASSIGNED_FILTER_ID);

      // Only apply filter when some options are selected (not none and not all)
      // None selected = show all (no filter)
      // All selected = show all (no filter)
      // Some selected = filter by those
      const noneSelected = selected.length === 0;
      const allSelected = selected.length === totalOptions;

      if (!noneSelected && !allSelected) {
        // Some selected - apply filter
        if (controllerIds.length > 0) {
          params.controllerIds = controllerIds;
        }
        if (includeUnassigned) {
          params.includeUnassigned = true;
        }
        // If only unassigned selected (no controllers)
        if (controllerIds.length === 0 && includeUnassigned) {
          params.unassignedOnly = true;
          delete params.includeUnassigned;
        }
      }
      // If none selected or all selected, don't filter (show all)

      if (memberId) {
        params.memberId = memberId;
      }

      const data = await fetchEventsFunction(params);
      processEventsData(data);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setLoading(false);
    }
  }, [memberId, fetchEventsFunction, processEventsData]);

  // Track if controllers have been loaded
  const [controllersLoaded, setControllersLoaded] = useState(false);

  // Expose refresh function to parent via callback
  useEffect(() => {
    if (onRefreshRef) {
      const refreshFn = () => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          const start = calendarApi.view.activeStart;
          const end = calendarApi.view.activeEnd;
          fetchEvents(start, end);
        }
      };
      onRefreshRef(refreshFn);
    }
  }, [onRefreshRef, fetchEvents]);

  // Fetch all controllers on mount for the filter dropdown
  useEffect(() => {
    const loadAllControllers = async () => {
      if (fetchControllersFunction) {
        try {
          const data = await fetchControllersFunction();
          const controllersList = data.controllers || [];
          setAllControllers(controllersList);
          allControllersCountRef.current = controllersList.length;
          // Load colors from backend
          const colorsFromBackend = {};
          controllersList.forEach(controller => {
            if (controller.color) {
              colorsFromBackend[controller.id] = controller.color;
            }
          });
          if (Object.keys(colorsFromBackend).length > 0) {
            setControllerColors(prev => ({ ...colorsFromBackend, ...prev }));
          }
          setControllersLoaded(true);
        } catch (err) {
          console.error('Error fetching all controllers:', err);
        }
      }
    };
    loadAllControllers();
  }, [fetchControllersFunction]);

  // Apply defaultSelectedControllers when controllers are loaded
  useEffect(() => {
    if (!controllersLoaded && fetchControllersFunction) {
      // Wait for controllers to load
      return;
    }

    // Apply the default selection from URL
    if (defaultSelectedControllers && defaultSelectedControllers.length > 0) {
      // Only update if different from current selection
      const currentStr = JSON.stringify([...selectedControllersRef.current].sort());
      const newStr = JSON.stringify([...defaultSelectedControllers].sort());
      if (currentStr !== newStr) {
        selectedControllersRef.current = defaultSelectedControllers;
        setSelectedControllersState(defaultSelectedControllers);
        setFilterTrigger(prev => prev + 1);
      }
    } else {
      // null or empty array means no filter - show all (empty selection)
      selectedControllersRef.current = [];
      setSelectedControllersState([]);
    }
  }, [controllersLoaded, defaultSelectedControllers, fetchControllersFunction]);

  // Fetch events when date range or view changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const start = calendarApi.view.activeStart;
      const end = calendarApi.view.activeEnd;
      fetchEvents(start, end);
    }
  }, [currentDate, view, fetchEvents]);

  // Refetch events when filter trigger changes (user-initiated filter change)
  useEffect(() => {
    if (filterTrigger > 0 && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const start = calendarApi.view.activeStart;
      const end = calendarApi.view.activeEnd;
      fetchEvents(start, end);
    }
  }, [filterTrigger, fetchEvents]);

  // Handle filter changes - update ref and trigger refetch
  const handleFilterChange = useCallback((newSelection) => {
    selectedControllersRef.current = newSelection;
    setSelectedControllers(newSelection);
    // Trigger refetch
    setFilterTrigger(prev => prev + 1);
  }, [setSelectedControllers]);

  // Get item label based on count (uses props if provided, otherwise falls back to translations)
  const getItemLabel = useCallback((count) => {
    if (count === 1) {
      return itemLabelSingular || t('calendar.item-singular', { defaultValue: 'item' });
    }
    return itemLabelPlural || t('calendar.item-plural', { defaultValue: 'items' });
  }, [itemLabelSingular, itemLabelPlural, t]);

  // Convert events to FullCalendar format
  const calendarEvents = useMemo(() => events.map(event => {
    const controllerName = event.controller
      ? `${event.controller.firstName} ${event.controller.lastName}`
      : t('calendar.unassigned');
    const itemText = getItemLabel(event.vehicleCount);
    const baseColor = event.controller ? (controllerColors[event.controller.id] || UNASSIGNED_COLOR) : UNASSIGNED_COLOR;

    // Check if event has time (for timed events vs all-day events)
    const hasTime = event.dateTime && event.time && event.time !== '00:00:00';

    // Check if event is canceled
    const isCanceled = event.status === CONTROL_STATUS.CANCELED;

    // Check if event is in the past and its completion status
    const eventDate = hasTime ? event.dateTime : event.date;
    const isPast = isEventPast(eventDate);
    const isCompleted = isEventCompleted(event.conformity);
    // Canceled events are NOT considered overdue
    const isOverdue = isPast && !isCompleted && !isCanceled;

    // Determine the display color based on status
    let displayColor = baseColor;
    let textColor = '#ffffff';

    if (isOverdue) {
      // Past + not completed + not canceled: use overdue color with transparency
      displayColor = hexToRgba(OVERDUE_COLOR, 0.5);
      textColor = '#b71c1c'; // Dark red text for better contrast
    }

    // Build title - use custom getEventTitle if provided, otherwise use controller name
    // Status is indicated visually through colors, not text prefixes
    const customTitle = getEventTitle ? getEventTitle(event, { itemLabel: itemText }) : null;
    const title = hasTime
      ? (customTitle || controllerName)
      : `${customTitle || controllerName} - ${event.vehicleCount} ${itemText}`;

    return {
      id: event.id,
      title,
      start: hasTime ? event.dateTime : event.date,
      end: hasTime ? moment(event.dateTime).add(30, 'minutes').toISOString() : moment(event.date).endOf('day').toISOString(),
      allDay: !hasTime,
      editable: event.editable === true && !isCanceled, // Canceled events are not editable
      extendedProps: {
        ...event,
        controllerName,
        color: baseColor,
        isPast,
        isCompleted,
        isOverdue,
        isCanceled
      },
      backgroundColor: displayColor,
      borderColor: isOverdue ? OVERDUE_COLOR : displayColor,
      textColor
    };
  }), [events, controllerColors, t, getItemLabel, getEventTitle]);

  // Handle event click - show control details modal or day list based on context
  const handleEventClick = (clickInfo) => {
    const eventData = clickInfo.event.extendedProps;
    const currentViewType = clickInfo.view.type;

    // Check if event has a specific time (CATP) or is aggregated (Betterfield)
    // Aggregated events don't have a specific time or have time as '00:00:00'
    const hasSpecificTime = eventData.time && eventData.time !== '00:00:00';

    // For aggregated events (Betterfield) with multiple resources, show list first
    if (!hasSpecificTime && eventData.vehicleCount > 1) {
      // Show day events dialog with this event's resources
      setSelectedDate(clickInfo.event.start);
      setDayEvents([eventData]);
      setDayDialogOpen(true);
      return;
    }

    // In month view with multiple aggregated events on same day, show day list first
    if (currentViewType === 'dayGridMonth' && !hasSpecificTime) {
      const dateStr = eventData.date || moment(clickInfo.event.start).format('YYYY-MM-DD');
      const dayEventsList = events.filter(e => e.date === dateStr);

      if (dayEventsList.length > 1) {
        // Multiple aggregated events - show day events list
        setSelectedDate(clickInfo.event.start);
        setDayEvents(dayEventsList);
        setDayDialogOpen(true);
        return;
      }
    }

    // Single event, timed event, or week/day view - show details directly
    if (renderControlDetails) {
      setSelectedControl(eventData);
      setControlDialogOpen(true);
    } else if (onControlClick) {
      onControlClick(eventData);
    }
  };

  // Close control details modal
  const handleCloseControlDetails = () => {
    setControlDialogOpen(false);
    setSelectedControl(null);
  };

  // Navigate to full control details page
  const handleViewFullDetails = () => {
    if (selectedControl && onControlClick) {
      setControlDialogOpen(false);
      setDayDialogOpen(false); // Also close list when navigating
      onControlClick(selectedControl);
    }
  };

  // Handle event drop (drag and drop rescheduling)
  const handleEventDrop = useCallback(async (dropInfo) => {
    const { event, revert } = dropInfo;
    const eventData = event.extendedProps;

    // Check if update function is provided
    if (!updateEventDateFunction) {
      revert();
      return;
    }

    try {
      // Call the update function with type, id, and new date
      await updateEventDateFunction(
        eventData.type,
        eventData.controlId,
        event.start.toISOString()
      );

      // Update local event data
      const updatedEvents = events.map(e => {
        if (e.id === eventData.id) {
          return {
            ...e,
            date: moment(event.start).format('YYYY-MM-DD'),
            dateTime: event.start.toISOString()
          };
        }
        return e;
      });
      setEvents(updatedEvents);
    } catch (error) {
      console.error('Failed to update event date:', error);
      // Revert the drag if update fails
      revert();
      // Refetch events to reset calendar state - ensures subsequent drags work
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const start = calendarApi.view.activeStart;
        const end = calendarApi.view.activeEnd;
        fetchEvents(start, end);
      }
      // Get error code and translate it
      const errorCode = error?.response?.data?.errorCode;
      const errorMessage = errorCode
        ? t(`calendar.drag-drop.errors.${errorCode}`, { defaultValue: t('calendar.drag-drop.error') })
        : t('calendar.drag-drop.error');
      // Show error using callback or internal snackbar
      if (onError) {
        onError(errorMessage);
      } else {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  }, [updateEventDateFunction, events, t, onError, fetchEvents]);

  // Handle cancel event (for behind schedule controls)
  const handleCancelEvent = useCallback(async (eventData) => {
    if (!cancelEventFunction) {
      return;
    }

    try {
      await cancelEventFunction(eventData.type, eventData.controlId);

      // Update local event data to mark as canceled
      // Match by controlId since eventData may come from vehicles array which has controlId, not id
      const targetControlId = eventData.controlId;
      const updatedEvents = events.map(e => {
        if (e.controlId === targetControlId) {
          return {
            ...e,
            status: CONTROL_STATUS.CANCELED,
            // Also update vehicles array if present
            vehicles: e.vehicles?.map(v =>
              v.controlId === targetControlId ? { ...v, status: CONTROL_STATUS.CANCELED } : v
            )
          };
        }
        return e;
      });
      setEvents(updatedEvents);

      // Also update dayEvents if dialog is open
      setDayEvents(prev => prev.map(e => {
        if (e.controlId === targetControlId) {
          return {
            ...e,
            status: CONTROL_STATUS.CANCELED,
            vehicles: e.vehicles?.map(v =>
              v.controlId === targetControlId ? { ...v, status: CONTROL_STATUS.CANCELED } : v
            )
          };
        }
        // Also check inside vehicles array for multi-vehicle events
        if (e.vehicles?.some(v => v.controlId === targetControlId)) {
          return {
            ...e,
            vehicles: e.vehicles.map(v =>
              v.controlId === targetControlId ? { ...v, status: CONTROL_STATUS.CANCELED } : v
            )
          };
        }
        return e;
      }));

      // Update selectedControl if it's the one being canceled
      if (selectedControl && selectedControl.controlId === targetControlId) {
        setSelectedControl(prev => ({
          ...prev,
          status: CONTROL_STATUS.CANCELED
        }));
      }

      // Show success message
      const successMessage = t('calendar.cancel.success');
      if (onError) {
        // Use onError for notifications (could be renamed to onNotification)
        // For now, we'll use snackbar for success
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to cancel event:', error);
      // Get error code and translate it
      const errorCode = error?.response?.data?.errorCode;
      const errorMessage = errorCode
        ? t(`calendar.cancel.errors.${errorCode}`, { defaultValue: t('calendar.cancel.error') })
        : t('calendar.cancel.error');
      // Show error using callback or internal snackbar
      if (onError) {
        onError(errorMessage);
      } else {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  }, [cancelEventFunction, events, t, onError, selectedControl]);

  // Handle assign controller to a pending control
  const handleAssignController = useCallback(async (eventData, controllerId) => {
    if (!assignControllerFunction) {
      return;
    }

    try {
      const result = await assignControllerFunction(eventData.type, eventData.controlId, controllerId);

      // Find the assigned controller from allControllers
      const assignedController = allControllers.find(c => c.id === controllerId);

      // Update local event data with the assigned controller
      const updatedEvents = events.map(e => {
        if (e.id === eventData.id) {
          return {
            ...e,
            controllerId,
            controller: assignedController || result?.control?.controller
          };
        }
        return e;
      });
      setEvents(updatedEvents);

      // Also update dayEvents if dialog is open
      setDayEvents(prev => prev.map(e => {
        if (e.id === eventData.id) {
          return {
            ...e,
            controllerId,
            controller: assignedController || result?.control?.controller
          };
        }
        return e;
      }));

      // Update selectedControl if it's the one being assigned
      if (selectedControl && selectedControl.id === eventData.id) {
        setSelectedControl(prev => ({
          ...prev,
          controllerId,
          controller: assignedController || result?.control?.controller
        }));
      }

      // Show success message
      const successMessage = t('calendar.assign.success');
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to assign controller:', error);
      const errorMessage = t('calendar.assign.error');
      if (onError) {
        onError(errorMessage);
      } else {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  }, [assignControllerFunction, events, allControllers, selectedControl, t, onError]);

  // Helper to show day events dialog
  const showDayEvents = useCallback((date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const dayEventsList = events.filter(e => e.date === dateStr);

    if (dayEventsList.length > 0) {
      // Has events - open day events dialog
      setSelectedDate(date);
      setDayEvents(dayEventsList);
      setDayDialogOpen(true);
    } else if (onCreateControl && canCreateControl && isDateTodayOrFuture(date)) {
      // No events but future date - directly open create control (if permission allows)
      onCreateControl(date);
    }

    if (onDateClick) {
      onDateClick(date, dayEventsList);
    }
  }, [events, onDateClick, onCreateControl, canCreateControl]);

  // Handle date click
  const handleDateClick = (clickInfo) => {
    // Month view: clicking on cell shows events list
    // Week/Day views: all-day area click shows events list, time slot click creates control
    if (view === 'dayGridMonth' || clickInfo.allDay) {
      showDayEvents(clickInfo.date);
    } else if (onCreateControl && canCreateControl && isDateTodayOrFuture(clickInfo.date)) {
      // Week/Day view time slot click - directly create control with specific time (if permission allows)
      onCreateControl(clickInfo.date);
    }
  };

  // Handle day header click (for week/day views)
  const handleNavLinkDayClick = (date) => {
    showDayEvents(date);
  };

  // Handle view change
  const handleViewChange = (viewName) => {
    setView(viewName);
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(viewName);
    }
  };

  // Handle selection change from Select component
  const handleSelectionChange = (event) => {
    const { value } = event.target;

    // Check if "Select All" was clicked
    if (value.includes(SELECT_ALL_ID)) {
      const totalOptions = allControllers.length + 1; // +1 for unassigned
      if (selectedControllers.length === totalOptions) {
        // All were selected, uncheck all (back to default - no filter)
        handleFilterChange([]);
      } else {
        // Not all selected, select all
        handleFilterChange([...allControllers.map(c => c.id), UNASSIGNED_FILTER_ID]);
      }
    } else {
      // Normal selection change
      handleFilterChange(value);
    }
  };

  // Navigate prev/next
  const navigateCalendar = (direction) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (direction === 'prev') {
        calendarApi.prev();
      } else {
        calendarApi.next();
      }
      setCurrentDate(calendarApi.getDate());
    }
  };

  // Go to specific month/year
  const goToMonthYear = (month, year) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const newDate = new Date(year, month, 1);
      calendarApi.gotoDate(newDate);
      setCurrentDate(newDate);
    }
  };

  // Go to today
  const goToToday = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.today();
      setCurrentDate(calendarApi.getDate());
    }
  };

  // Export day events to CSV
  const handleExportCSV = () => {
    if (dayEvents.length === 0) return;

    // Use custom export function if provided
    if (exportCSVFunction) {
      exportCSVFunction(dayEvents, {
        t,
        selectedDate,
        isEventPast,
        isEventCompleted,
        CONTROL_STATUS,
        moment
      });
      return;
    }

    // Default export with common data (for BF)
    const csvContent = [
      [
        t('calendar.export.date'),
        t('calendar.export.controller'),
        t('calendar.export.location'),
        t('calendar.export.conformity')
      ].join(','),
      ...dayEvents.map(event => {
        const controllerName = event.controller
          ? `${event.controller.firstName} ${event.controller.lastName}`
          : t('calendar.unassigned');
        const locationName = event.location?.name || 'N/A';
        const dateTime = event.dateTime
          ? moment(event.dateTime).format('DD/MM/YYYY HH:mm')
          : moment(event.date).format('DD/MM/YYYY');
        const getConformityLabel = (conformity) => {
          if (conformity === true || conformity === 1) return t('conformity.valid');
          if (conformity === false || conformity === 0) return t('conformity.invalid');
          return t('conformity.waiting-result');
        };
        return [
          dateTime,
          controllerName,
          locationName,
          getConformityLabel(event.conformity)
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inspections_${moment(selectedDate).format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card>
      <CardContent>
        {/* Title with Settings */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {title && (
            <Typography variant="h6" sx={{ color: titleColor, fontSize: titleFontSize }}>
              {title}
            </Typography>
          )}
          {!title && <Box />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {onImportCalendar && (
            <Tooltip title={t('calendar.import-calendar')}>
              <IconButton size="small" onClick={onImportCalendar} sx={{ color: mainColor }}>
                <FileDownload fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onExportTemplate && (
            <Tooltip title={t('calendar.export-template')}>
              <IconButton size="small" onClick={onExportTemplate} sx={{ color: mainColor }}>
                <FileUpload fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            onClick={async () => {
              setTempControllerColors({ ...controllerColors });
              setSettingsDialogOpen(true);
              // Fetch all controllers (including hidden) if function is provided
              if (fetchControllersFunction) {
                setSettingsLoading(true);
                try {
                  // Fetch with includeHidden=true for settings modal
                  const data = await fetchControllersFunction({ includeHidden: true });
                  const controllersList = data.controllers || [];
                  setAllControllers(controllersList);
                  // Initialize temp colors and visibility with saved values from backend
                  const colorsFromBackend = {};
                  const visibilityFromBackend = {};
                  controllersList.forEach(controller => {
                    if (controller.color) {
                      colorsFromBackend[controller.id] = controller.color;
                    }
                    visibilityFromBackend[controller.id] = controller.calendarVisible !== false;
                  });
                  setTempControllerColors(prev => ({ ...colorsFromBackend, ...prev }));
                  setTempControllerVisibility(visibilityFromBackend);
                  // Also update the main controller colors
                  setControllerColors(prev => ({ ...colorsFromBackend, ...prev }));
                } catch (err) {
                  console.error('Error fetching controllers:', err);
                } finally {
                  setSettingsLoading(false);
                }
              }
            }}
            size="small"
            title={t('calendar.settings.title')}
          >
            <Settings />
          </IconButton>
          </Box>
        </Box>
        {/* Header with view selector and navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ButtonGroup variant="outlined" size="small">
              <Button
                variant={view === 'dayGridMonth' ? 'contained' : 'outlined'}
                onClick={() => handleViewChange('dayGridMonth')}
                sx={view === 'dayGridMonth'
                  ? { backgroundColor: mainColor, borderColor: mainColor, '&:hover': { backgroundColor: mainColor, filter: 'brightness(0.9)' } }
                  : { borderColor: mainColor, color: mainColor }}
              >
                {t('calendar.view.month')}
              </Button>
              <Button
                variant={view === 'timeGridWeek' ? 'contained' : 'outlined'}
                onClick={() => handleViewChange('timeGridWeek')}
                sx={view === 'timeGridWeek'
                  ? { backgroundColor: mainColor, borderColor: mainColor, '&:hover': { backgroundColor: mainColor, filter: 'brightness(0.9)' } }
                  : { borderColor: mainColor, color: mainColor }}
              >
                {t('calendar.view.week')}
              </Button>
              <Button
                variant={view === 'timeGridDay' ? 'contained' : 'outlined'}
                onClick={() => handleViewChange('timeGridDay')}
                sx={view === 'timeGridDay'
                  ? { backgroundColor: mainColor, borderColor: mainColor, '&:hover': { backgroundColor: mainColor, filter: 'brightness(0.9)' } }
                  : { borderColor: mainColor, color: mainColor }}
              >
                {t('calendar.view.day')}
              </Button>
            </ButtonGroup>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => navigateCalendar('prev')} size="small">
                <ChevronLeft />
              </IconButton>
              <Typography
                variant="h6"
                onClick={(e) => {
                  setPickerYear(currentDate.getFullYear());
                  setDatePickerAnchor(e.currentTarget);
                }}
                sx={{
                  minWidth: 180,
                  textAlign: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    color: mainColor,
                  }
                }}
              >
                {moment(currentDate).format('MMMM YYYY')}
              </Typography>
              <IconButton onClick={() => navigateCalendar('next')} size="small">
                <ChevronRight />
              </IconButton>
              <Button
                onClick={goToToday}
                size="small"
                variant="outlined"
                disabled={moment(currentDate).isSame(moment(), 'day')}
                sx={{ borderColor: mainColor, color: mainColor }}
              >
                {t('calendar.today')}
              </Button>
            </Box>
          </Box>

          {/* Controller filter - show if fetchControllersFunction is available and showControllerFilter is true */}
          {fetchControllersFunction && showControllerFilter && (
            <FormControl
              size="small"
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': { borderColor: mainColor },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: mainColor },
              }}
            >
              <InputLabel>{t('calendar.filter.controllers')}</InputLabel>
              <Select
                multiple
                value={selectedControllers}
                onChange={handleSelectionChange}
                label={t('calendar.filter.controllers')}
                renderValue={(selected) => {
                  const totalOptions = allControllers.length + 1; // +1 for unassigned
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.length === 0 && (
                        <Chip size="small" label={t('calendar.filter.all')} />
                      )}
                      {selected.length > 0 && selected.length < totalOptions && (
                        <Chip size="small" label={`${selected.length} ${t('calendar.filter.selected')}`} />
                      )}
                      {selected.length === totalOptions && (
                        <Chip size="small" label={t('calendar.filter.all')} />
                      )}
                    </Box>
                  );
                }}
              >
                <MenuItem value={SELECT_ALL_ID}>
                  <Checkbox
                    checked={allControllers.length > 0 && selectedControllers.length === allControllers.length + 1}
                    indeterminate={selectedControllers.length > 0 && selectedControllers.length < allControllers.length + 1}
                    sx={{ '&.Mui-checked': { color: mainColor }, '&.MuiCheckbox-indeterminate': { color: mainColor } }}
                  />
                  <Typography component="span" sx={{ ml: 1 }}>
                    {t('calendar.filter.select-all')}
                  </Typography>
                </MenuItem>
                <Divider />
                {/* Unassigned option */}
                <MenuItem value={UNASSIGNED_FILTER_ID}>
                  <Checkbox
                    checked={selectedControllers.includes(UNASSIGNED_FILTER_ID)}
                    sx={{ '&.Mui-checked': { color: mainColor } }}
                  />
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 1,
                      backgroundColor: UNASSIGNED_COLOR,
                      mr: 1,
                      ml: 1
                    }}
                  />
                  {t('calendar.unassigned')}
                </MenuItem>
                <Divider />
                {allControllers.map(controller => (
                  <MenuItem key={controller.id} value={controller.id}>
                    <Checkbox
                      checked={selectedControllers.includes(controller.id)}
                      sx={{ '&.Mui-checked': { color: mainColor } }}
                    />
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: 1,
                        backgroundColor: controllerColors[controller.id] || UNASSIGNED_COLOR,
                        mr: 1,
                        ml: 1
                      }}
                    />
                    {controller.firstName} {controller.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Controller legend - shows controllers from current events */}
        {controllers.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {controllers.map(controller => {
              // No selection or all selected = show all at full opacity
              // Some selected = selected at full opacity, others faded
              const totalOptions = allControllers.length + 1;
              const noFilter = selectedControllers.length === 0 || selectedControllers.length === totalOptions;
              const isActive = noFilter || selectedControllers.includes(controller.id);
              return (
                <Chip
                  key={controller.id}
                  label={`${controller.firstName} ${controller.lastName}`}
                  sx={{
                    backgroundColor: controllerColors[controller.id] || UNASSIGNED_COLOR,
                    color: 'white',
                    opacity: isActive ? 1 : 0.4
                  }}
                  size="small"
                />
              );
            })}
          </Box>
        )}

        {/* Calendar */}
        <Box sx={{
          position: 'relative',
          height,
          '& .fc-event': {
            cursor: 'pointer'
          },
          '& .fc-timegrid-event': {
            cursor: 'pointer'
          },
          '& .fc-timegrid-event-harness': {
            cursor: 'pointer'
          },
          // Month view: future and today cells are clickable (can create events)
          '& .fc-daygrid-day:not(.fc-day-past)': {
            cursor: 'pointer'
          },
          // Past days: default cursor (no action available)
          '& .fc-daygrid-day.fc-day-past': {
            cursor: 'default'
          },
          // Past days with events: pointer cursor (can open events list)
          '& .fc-daygrid-day.fc-day-past:has(.fc-event)': {
            cursor: 'pointer'
          },
          // Week/Day views: day header links are clickable (navLinks)
          '& .fc-col-header-cell a': {
            cursor: 'pointer'
          },
          // Week/Day views: all-day area is clickable (except past without events)
          '& .fc-daygrid-day-events': {
            cursor: 'pointer'
          },
          '& .fc-day-past .fc-daygrid-day-events': {
            cursor: 'default'
          },
          '& .fc-day-past .fc-daygrid-day-events:has(.fc-event)': {
            cursor: 'pointer'
          },
          // Week/Day views: all-day day frames
          '& .fc-daygrid-day-frame': {
            cursor: 'pointer'
          },
          '& .fc-day-past .fc-daygrid-day-frame': {
            cursor: 'default'
          },
          '& .fc-day-past:has(.fc-event) .fc-daygrid-day-frame': {
            cursor: 'pointer'
          },
          // Week/Day views: time grid columns and slots are clickable
          '& .fc-timegrid-col': {
            cursor: 'pointer'
          },
          '& .fc-timegrid-slot': {
            cursor: 'pointer'
          },
          // Past day columns: default cursor unless they have events
          '& .fc-timegrid-col.fc-day-past': {
            cursor: 'default'
          },
          '& .fc-timegrid-col.fc-day-past:has(.fc-event)': {
            cursor: 'pointer'
          },
          ...calendarSx
        }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1000
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <FullCalendar
            key={`${i18n.language}-${initialViewRef.current}-${initialDateRef.current?.toISOString()}`}
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={view}
            initialDate={currentDate}
            headerToolbar={false}
            events={calendarEvents}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            editable={!!updateEventDateFunction && canRescheduleEvents}
            eventStartEditable={!!updateEventDateFunction && canRescheduleEvents}
            eventDurationEditable={false}
            eventDrop={handleEventDrop}
            navLinks
            navLinkDayClick={handleNavLinkDayClick}
            height={height}
            locale={getFullCalendarLocale()}
            firstDay={1} // Monday
            eventDisplay="block"
            dayMaxEvents={3}
            moreLinkClick={(info) => {
              // Open our custom modal instead of the default popover
              showDayEvents(info.date);
              return 'none'; // Prevent default popover
            }}
            displayEventEnd={false}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false
            }}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false
            }}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            scrollTime="08:00:00"
            dayHeaderFormat={{
              weekday: 'short',
              day: 'numeric',
              omitCommas: true
            }}
          />
        </Box>

        {/* Day events dialog */}
        <BaseDialog
          open={dayDialogOpen}
          onClose={() => {
            setDayDialogOpen(false);
            setExpandedEventId(null);
          }}
          title={t('calendar.day-events.title', { date: moment(selectedDate).format('DD MMMM YYYY') })}
          hideActions
          width="md"
          primaryColor={mainColor}
          titleAction={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {onCreateControl && canCreateControl && isDateTodayOrFuture(selectedDate) && (
                <Tooltip title={t('calendar.day-events.create-control')}>
                  <IconButton
                    onClick={() => {
                      onCreateControl(selectedDate);
                    }}
                    size="small"
                    sx={{ color: mainColor }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('calendar.day-events.export-csv')}>
                <span>
                  <IconButton
                    onClick={handleExportCSV}
                    disabled={dayEvents.length === 0}
                    size="small"
                    sx={{ color: mainColor }}
                  >
                    <GetApp />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          }
        >
          <Box sx={{ mt: 2 }}>
            {dayEvents.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('calendar.day-events.no-events')}
              </Typography>
            ) : (
              <List>
                {dayEvents.flatMap((event, eventIndex) => {
                  // For aggregated events with multiple vehicles, show each vehicle separately
                  const hasMultipleVehicles = event.vehicles && event.vehicles.length > 1;

                  if (hasMultipleVehicles) {
                    return event.vehicles.map((vehicle, vehicleIndex) => {
                      const vehicleEventId = `${event.id}-${vehicle.controlId || vehicleIndex}`;
                      const isExpanded = expandedEventId === vehicleEventId;
                      // Create a control object for this specific vehicle
                      const vehicleControl = {
                        ...event,
                        controlId: vehicle.controlId,
                        resource: { name: vehicle.resourceName },
                        resourceType: { name: vehicle.resourceType },
                        conformity: vehicle.conformity,
                        status: vehicle.status,
                        vehicleCount: 1
                      };
                      const eventDate = vehicle.dateTime || event.dateTime || event.date;
                      const isCanceled = vehicle.status === CONTROL_STATUS.CANCELED;
                      const isOverdue = isEventPast(eventDate) && !isEventCompleted(vehicle.conformity) && !isCanceled;

                      return (
                        <React.Fragment key={vehicleEventId}>
                          <ListItem
                            button
                            onClick={() => {
                              if (renderControlDetails) {
                                // Toggle expand/collapse - only one at a time
                                setExpandedEventId(isExpanded ? null : vehicleEventId);
                              } else {
                                setDayDialogOpen(false);
                                if (onControlClick) {
                                  onControlClick(vehicleControl);
                                }
                              }
                            }}
                            sx={{
                              backgroundColor: isExpanded
                                ? 'action.selected'
                                : isOverdue
                                  ? hexToRgba(OVERDUE_COLOR, 0.1)
                                  : 'transparent'
                            }}
                          >
                            <ListItemText
                              primary={
                                renderEventListItem
                                  ? renderEventListItem(vehicleControl, { controllerColors, t, UNASSIGNED_COLOR }).primary
                                  : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: 1,
                                          backgroundColor: event.controller
                                            ? (controllerColors[event.controller.id] || UNASSIGNED_COLOR)
                                            : UNASSIGNED_COLOR
                                        }}
                                      />
                                      <Typography variant="subtitle1">
                                        {event.controller
                                          ? `${event.controller.firstName} ${event.controller.lastName}`
                                          : t('calendar.unassigned')}
                                      </Typography>
                                    </Box>
                                  )
                              }
                              secondary={
                                renderEventListItem
                                  ? renderEventListItem(vehicleControl, { controllerColors, t, UNASSIGNED_COLOR }).secondary
                                  : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {vehicle.resourceName || 'N/A'}
                                    </Typography>
                                  )
                              }
                            />
                            {renderControlDetails && (
                              <ListItemSecondaryAction sx={{ pointerEvents: 'none' }}>
                                {isExpanded ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
                              </ListItemSecondaryAction>
                            )}
                          </ListItem>
                          {/* Inline control details */}
                          {renderControlDetails && (
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{ pl: 2, pr: 2, pb: 2, pt: 1 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {renderControlDetails(vehicleControl, {
                                  onClose: () => setExpandedEventId(null),
                                  onViewFullDetails: () => {
                                    // Keep modal and item expanded since we're opening in new tab
                                    if (onControlClick) {
                                      onControlClick(vehicleControl);
                                    }
                                  },
                                  onCancel: isOverdue && cancelEventFunction && canCancelOverdueEvents ? () => handleCancelEvent(vehicleControl) : null,
                                  onAssign: assignControllerFunction && canAssignController ? (controllerId) => handleAssignController(vehicleControl, controllerId) : null,
                                  controllers: allControllers,
                                  controllerColors,
                                  mainColor,
                                  inline: true,
                                  isOverdue
                                })}
                              </Box>
                            </Collapse>
                          )}
                          {(vehicleIndex < event.vehicles.length - 1 || eventIndex < dayEvents.length - 1) && <Divider />}
                        </React.Fragment>
                      );
                    });
                  }

                  // Single vehicle or CATP-style event - expandable
                  const isExpanded = expandedEventId === event.id;
                  const eventDate = event.dateTime || event.date;
                  const isCanceled = event.status === CONTROL_STATUS.CANCELED;
                  const isOverdue = isEventPast(eventDate) && !isEventCompleted(event.conformity) && !isCanceled;
                  return (
                    <React.Fragment key={event.id}>
                      <ListItem
                        button
                        onClick={() => {
                          if (renderControlDetails) {
                            // Toggle expand/collapse - only one at a time
                            setExpandedEventId(isExpanded ? null : event.id);
                          } else {
                            setDayDialogOpen(false);
                            if (onControlClick) {
                              onControlClick(event);
                            }
                          }
                        }}
                        sx={{
                          backgroundColor: isExpanded
                            ? 'action.selected'
                            : isOverdue
                              ? hexToRgba(OVERDUE_COLOR, 0.1)
                              : 'transparent'
                        }}
                      >
                        <ListItemText
                          primary={
                            renderEventListItem
                              ? renderEventListItem(event, { controllerColors, t, UNASSIGNED_COLOR }).primary
                              : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: 1,
                                      backgroundColor: event.controller
                                        ? (controllerColors[event.controller.id] || UNASSIGNED_COLOR)
                                        : UNASSIGNED_COLOR
                                    }}
                                  />
                                  <Typography variant="subtitle1">
                                    {event.controller
                                      ? `${event.controller.firstName} ${event.controller.lastName}`
                                      : t('calendar.unassigned')}
                                  </Typography>
                                </Box>
                              )
                          }
                          secondary={
                            renderEventListItem
                              ? renderEventListItem(event, { controllerColors, t, UNASSIGNED_COLOR }).secondary
                              : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {event.resource?.name || 'N/A'}
                                </Typography>
                              )
                          }
                        />
                        {renderControlDetails && (
                          <ListItemSecondaryAction sx={{ pointerEvents: 'none' }}>
                            {isExpanded ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                      {/* Inline control details */}
                      {renderControlDetails && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{ pl: 2, pr: 2, pb: 2, pt: 1 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {renderControlDetails(event, {
                              onClose: () => setExpandedEventId(null),
                              onViewFullDetails: () => {
                                // Keep modal and item expanded since we're opening in new tab
                                if (onControlClick) {
                                  onControlClick(event);
                                }
                              },
                              onCancel: isOverdue && cancelEventFunction && canCancelOverdueEvents ? () => handleCancelEvent(event) : null,
                              onAssign: assignControllerFunction && canAssignController ? (controllerId) => handleAssignController(event, controllerId) : null,
                              controllers: allControllers,
                              controllerColors,
                              mainColor,
                              inline: true,
                              isOverdue
                            })}
                          </Box>
                        </Collapse>
                      )}
                      {eventIndex < dayEvents.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </BaseDialog>

        {/* Control Details Modal - uses custom render function */}
        {renderControlDetails && (
          <BaseDialog
            open={controlDialogOpen}
            onClose={handleCloseControlDetails}
            title={t('calendar.control-details.title')}
            hideActions
            width="md"
            primaryColor={mainColor}
          >
            <Box sx={{ mt: 2 }}>
              {selectedControl && (() => {
                const controlDate = selectedControl.dateTime || selectedControl.date;
                const controlIsOverdue = isEventPast(controlDate) && !isEventCompleted(selectedControl.conformity);
                return renderControlDetails(selectedControl, {
                  onClose: handleCloseControlDetails,
                  onViewFullDetails: handleViewFullDetails,
                  onCancel: controlIsOverdue && cancelEventFunction && canCancelOverdueEvents ? () => handleCancelEvent(selectedControl) : null,
                  onAssign: assignControllerFunction && canAssignController ? (controllerId) => handleAssignController(selectedControl, controllerId) : null,
                  controllers: allControllers,
                  controllerColors,
                  mainColor,
                  isOverdue: controlIsOverdue
                });
              })()}
            </Box>
          </BaseDialog>
        )}

        {/* Month/Year Picker Popover */}
        <Popover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={() => setDatePickerAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 2, width: 280 }}>
            {/* Year selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <IconButton size="small" onClick={() => setPickerYear(prev => prev - 1)}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {pickerYear}
              </Typography>
              <IconButton size="small" onClick={() => setPickerYear(prev => prev + 1)}>
                <ChevronRight />
              </IconButton>
            </Box>
            {/* Month grid */}
            <Grid container spacing={1}>
              {moment.monthsShort().map((month, index) => (
                <Grid item xs={4} key={month}>
                  <Button
                    fullWidth
                    variant={
                      currentDate.getMonth() === index && currentDate.getFullYear() === pickerYear
                        ? 'contained'
                        : 'text'
                    }
                    size="small"
                    onClick={() => {
                      goToMonthYear(index, pickerYear);
                      setDatePickerAnchor(null);
                    }}
                    sx={{
                      textTransform: 'capitalize',
                      minWidth: 0,
                      py: 1,
                      ...(currentDate.getMonth() === index && currentDate.getFullYear() === pickerYear && {
                        backgroundColor: mainColor,
                        '&:hover': {
                          backgroundColor: mainColor,
                          filter: 'brightness(0.9)',
                        },
                      }),
                    }}
                  >
                    {month}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Popover>

        {/* Settings Dialog */}
        <BaseDialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          title={t('calendar.settings.title')}
          hideActions
          width="sm"
          primaryColor={mainColor}
        >
          <Box sx={{ mt: 2 }}>
            {settingsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (allControllers.length > 0 ? allControllers : controllers).length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                {t('calendar.settings.color-matching.no-controllers')}
              </Typography>
            ) : (
              <>
                {/* Controller Visibility Section - Only show if visibility function is provided */}
                {updateControllerVisibilityFunction && (
                  <Accordion defaultExpanded sx={{ mb: 1, boxShadow: 'none', border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t('calendar.settings.visibility.title', 'Controller Visibility')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('calendar.settings.visibility.description', 'Choose which controllers appear in the calendar filters.')}
                      </Typography>
                      <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                        {(allControllers.length > 0 ? allControllers : controllers).map((controller) => (
                          <Box
                            key={controller.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 0.5,
                              px: 1,
                              borderBottom: '1px solid #f0f0f0',
                              '&:last-child': { borderBottom: 'none' }
                            }}
                          >
                            <Typography variant="body2">
                              {controller.firstName} {controller.lastName}
                            </Typography>
                            <Switch
                              checked={tempControllerVisibility[controller.id] !== false}
                              onChange={(e) => {
                                setTempControllerVisibility((prev) => ({
                                  ...prev,
                                  [controller.id]: e.target.checked
                                }));
                              }}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: mainColor },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: mainColor }
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Color Matching Section */}
                {(() => {
                  // When visibility feature is enabled, only show visible controllers
                  // Otherwise show all controllers
                  const controllersForColors = updateControllerVisibilityFunction
                    ? (allControllers.length > 0 ? allControllers : controllers)
                      .filter(controller => tempControllerVisibility[controller.id] !== false)
                    : (allControllers.length > 0 ? allControllers : controllers);

                  const colorContent = controllersForColors.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                      {t('calendar.settings.color-matching.no-visible-controllers', 'No visible controllers to configure.')}
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: updateControllerVisibilityFunction ? 250 : 300, overflowY: 'auto' }}>
                      {controllersForColors.map((controller) => (
                        <Box
                          key={controller.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                            px: 1,
                            borderBottom: '1px solid #f0f0f0',
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              data-color-preview
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                backgroundColor: tempControllerColors[controller.id] || UNASSIGNED_COLOR,
                                border: '1px solid #ccc'
                              }}
                            />
                            <Typography variant="body2">
                              {controller.firstName} {controller.lastName}
                            </Typography>
                          </Box>
                          <input
                            type="color"
                            defaultValue={tempControllerColors[controller.id] || UNASSIGNED_COLOR}
                            onBlur={(e) => {
                              setTempControllerColors((prev) => ({
                                ...prev,
                                [controller.id]: e.target.value
                              }));
                            }}
                            onChange={(e) => {
                              // Update preview box color without triggering full re-render
                              const previewBox = e.target.parentElement?.querySelector('[data-color-preview]');
                              if (previewBox) {
                                previewBox.style.backgroundColor = e.target.value;
                              }
                            }}
                            style={{
                              width: 40,
                              height: 30,
                              padding: 0,
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              cursor: 'pointer'
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  );

                  // With visibility feature: use accordion
                  // Without visibility feature: simple layout (Betterfield)
                  return updateControllerVisibilityFunction ? (
                    <Accordion sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {t('calendar.settings.color-matching.title')}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t('calendar.settings.color-matching.description')}
                        </Typography>
                        {colorContent}
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {t('calendar.settings.color-matching.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('calendar.settings.color-matching.description')}
                      </Typography>
                      {colorContent}
                    </>
                  );
                })()}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setSettingsDialogOpen(false)}
                disabled={savingColors}
                sx={{ borderColor: mainColor, color: mainColor }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={savingColors}
                onClick={async () => {
                  setSavingColors(true);
                  try {
                    const controllersToUpdate = allControllers.length > 0 ? allControllers : controllers;
                    const savePromises = [];

                    // Save visibility changes
                    if (updateControllerVisibilityFunction) {
                      controllersToUpdate
                        .filter(controller => {
                          const currentVisible = controller.calendarVisible !== false;
                          const newVisible = tempControllerVisibility[controller.id] !== false;
                          return currentVisible !== newVisible;
                        })
                        .forEach(controller => {
                          savePromises.push(
                            updateControllerVisibilityFunction(controller.id, tempControllerVisibility[controller.id] !== false)
                          );
                        });
                    }

                    // Save color changes
                    if (updateControllerColorFunction) {
                      controllersToUpdate
                        .filter(controller => tempControllerColors[controller.id] && tempControllerColors[controller.id] !== controller.color)
                        .forEach(controller => {
                          savePromises.push(
                            updateControllerColorFunction(controller.id, tempControllerColors[controller.id])
                          );
                        });
                    }

                    await Promise.all(savePromises);

                    // Refresh controllers list to get updated visibility
                    if (fetchControllersFunction) {
                      const data = await fetchControllersFunction();
                      const visibleControllers = data.controllers || [];
                      setAllControllers(visibleControllers);
                      allControllersCountRef.current = visibleControllers.length;

                      // Update selected controllers to remove any that are now hidden
                      const visibleControllerIds = new Set(visibleControllers.map(c => c.id));
                      const updatedSelection = selectedControllersRef.current.filter(
                        id => id === UNASSIGNED_FILTER_ID || visibleControllerIds.has(id)
                      );

                      // Update selection if it changed
                      if (updatedSelection.length !== selectedControllersRef.current.length) {
                        selectedControllersRef.current = updatedSelection;
                        setSelectedControllers(updatedSelection);
                      }

                      // Trigger calendar refetch
                      setFilterTrigger(prev => prev + 1);
                    }
                  } catch (err) {
                    console.error('Error saving settings:', err);
                  } finally {
                    setSavingColors(false);
                  }
                  setControllerColors(tempControllerColors);
                  setSettingsDialogOpen(false);
                }}
                sx={{ backgroundColor: mainColor, '&:hover': { backgroundColor: mainColor, filter: 'brightness(0.9)' } }}
              >
                {savingColors ? <CircularProgress size={20} color="inherit" /> : t('common.confirm')}
              </Button>
            </Box>
          </Box>
        </BaseDialog>

        {/* Snackbar - always shown for success, only for errors if onError callback is not provided */}
        <Snackbar
          open={snackbar.open && (snackbar.severity === 'success' || !onError)}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            elevation={6}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}
